import type { ICellRendererParams } from 'ag-grid-community';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { UserBrandInsight } from '../types';
import { BrandFavicon } from '../components/BrandFavicon';
import { RelationshipBadge } from '../components/visibility/VisibilityBadges';
import {
  canAddInsightAsCompetitor,
  resolveInsightRelationship,
} from '../utils/brand-relationship';
import { getHelpDescription } from '../utils/help/help-content';

export interface BrandRow extends UserBrandInsight {
  _rowIndex: number;
  _rowKey: string;
}

export interface BrandsColumnCallbacks {
  showDebug: boolean;
  showActions: boolean;
  addingKey: string | null;
  compact?: boolean;
  onAdd: (brand: BrandRow, key: string) => void;
}

export function brandsColumnConfig(callbacks: BrandsColumnCallbacks) {
  const compact = callbacks.compact === true;
  const cols: Array<Record<string, unknown>> = [
    {
      field: '_rowIndex',
      title: '#',
      type: 'string',
      width: compact ? 44 : 52,
      searchable: false,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<BrandRow>) => {
        const index = params.data?._rowIndex;
        if (index == null) return null;
        return (
          <span className="d-flex align-items-center justify-content-center h-100 w-100 text-500">
            {index + 1}
          </span>
        );
      },
    },
    {
      field: 'detectedHost',
      title: 'لوگو',
      type: 'string',
      width: compact ? 52 : 64,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<BrandRow>) => {
        const row = params.data;
        if (!row) return null;
        const displayName = row.canonicalName ?? row.brandName;
        return (
          <div className="d-flex align-items-center justify-content-center h-100 w-100">
            <BrandFavicon
              host={row.detectedHost}
              name={displayName}
              relationship={row.relationship}
              brandType={row.brandType}
              colorIndex={row._rowIndex}
              size={compact ? 'xs' : 'sm'}
            />
          </div>
        );
      },
    },
    {
      field: 'brandName',
      title: 'برند',
      type: 'string',
      width: 160,
      minWidth: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandRow>) => {
        const row = params.data;
        if (!row) return null;
        const displayName = row.canonicalName ?? row.brandName;
        if (compact) {
          return (
            <div className="d-flex align-items-center h-100 min-w-0">
              <span className="fw-medium text-800 text-truncate">{displayName}</span>
            </div>
          );
        }
        return (
          <div>
            <span className="fw-medium text-800 d-block">{displayName}</span>
            {row.matchedAlias && row.matchedAlias !== displayName && (
              <span className="fs--1 text-500">تطبیق: {row.matchedAlias}</span>
            )}
            {row.detectedHost && (
              <span className="fs--1 text-500 d-block">{row.detectedHost}</span>
            )}
            {callbacks.showDebug && row.sourceExcerpt && (
              <span className="fs--2 text-500 d-block mt-1" style={{ maxWidth: '20rem' }}>
                {row.sourceExcerpt}
              </span>
            )}
          </div>
        );
      },
    },
    {
      field: 'relationship',
      title: 'نوع',
      type: 'string',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandRow>) =>
        params.data ? (
          <RelationshipBadge relationship={resolveInsightRelationship(params.data)} solid />
        ) : null,
    },
    {
      field: 'position',
      title: 'رتبه',
      type: 'number',
      width: 90,
      searchable: true,
      valueFormatter: (p: { value: number | null }) => (p.value != null ? `#${p.value}` : '—'),
    },
  ];

  if (callbacks.showDebug) {
    cols.push(
      {
        field: 'mentions',
        title: 'تکرار در متن',
        type: 'number',
        width: 120,
        searchable: true,
      },
      {
        field: 'surfaceText',
        title: 'سطح',
        type: 'string',
        width: 140,
        searchable: true,
        valueFormatter: (p: { value: string | null | undefined }) => p.value ?? '—',
      },
      {
        field: 'detectionSource',
        title: 'منبع',
        type: 'string',
        width: 110,
        searchable: true,
        valueFormatter: (p: { value: string | null | undefined }) => p.value ?? '—',
      },
      {
        field: 'confidence',
        title: 'اطمینان',
        type: 'number',
        width: 100,
        searchable: true,
        valueFormatter: (p: { value: number | null | undefined }) =>
          p.value != null ? p.value.toFixed(2) : '—',
      },
      {
        field: 'provider',
        title: 'پرووایدر',
        type: 'string',
        width: 120,
        searchable: true,
        valueFormatter: (p: { value: string | null | undefined }) => p.value ?? '—',
      },
    );
  }

  cols.push(
    {
      field: 'shareOfVoice',
      title: 'سهم از دیده‌شدن',
      tooltip: getHelpDescription('metrics.share_of_voice'),
      type: 'number',
      width: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandRow>) => {
        const value = params.data?.shareOfVoice;
        if (value == null) return <span className="fs--1 text-300">—</span>;
        return <span>{value}%</span>;
      },
    },
    {
      field: 'visibilityContribution',
      title: 'دیده‌شدن در AI',
      type: 'number',
      flex: 1,
      minWidth: 130,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<BrandRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-pill bg-200 overflow-hidden flex-shrink-0"
              style={{ height: '0.375rem', width: '3.5rem' }}
            >
              <div
                className={`h-100 rounded-pill ${row.brandType === 'own_brand' ? 'bg-success' : 'bg-primary'}`}
                style={{ width: `${Math.min(Math.max(row.visibilityContribution, 0), 100)}%` }}
              />
            </div>
            <span className="fs--1 text-500" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {row.visibilityContribution}%
            </span>
          </div>
        );
      },
    },
  );

  if (callbacks.showActions) {
    cols.push({
      field: '_actions',
      title: 'عملیات',
      type: 'string',
      width: 130,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<BrandRow>) => {
        const row = params.data;
        if (!row) return null;
        if (!canAddInsightAsCompetitor(row)) {
          return (
            <div className="d-flex align-items-center justify-content-center h-100 w-100">
              {resolveInsightRelationship(row) === 'competitor' ? (
                <span className="badge badge-subtle-success rounded-pill">
                  <span className="d-inline-flex align-items-center gap-1">
                    <FontAwesomeIcon icon="check" className="fs--2" />
                    <span>در حال پیگیری</span>
                  </span>
                </span>
              ) : (
                <span className="fs--1 text-300">—</span>
              )}
            </div>
          );
        }
        const isAdding = callbacks.addingKey === row._rowKey;
        return (
          <div className="d-flex align-items-center justify-content-center h-100 w-100">
            <button
              type="button"
              disabled={isAdding}
              onClick={(e) => {
                e.stopPropagation();
                callbacks.onAdd(row, row._rowKey);
              }}
              className="btn btn-sm btn-falcon-primary py-0 px-2 fs--2"
            >
              {isAdding ? '…' : 'افزودن رقیب'}
            </button>
          </div>
        );
      },
    });
  }

  return cols;
}
